/**
 * FileDetailsPanel — Full formatted detail view of a single execution.
 *
 * Sections:
 * - Overview: key attributes rendered as KeyValuePairs
 * - Payments: paginated, filterable table of payment IDs linking to /payment
 * - Error Info: guarded JSON rendering (only when errorInfo is present)
 * - Execution Lifecycle: reuses the shared StatusTimeline component
 */
import { useEffect, useMemo, useState } from 'react';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Container from '@cloudscape-design/components/container';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { CopyableText } from 'src/components/common/CopyableText';
import { PhaseDetailsRenderer } from 'src/components/common/PhaseDetailsRenderer';
import { StatusTimeline } from 'src/components/common/StatusTimeline';
import { beaconApi } from 'src/services/beaconApi';
import { useAppSelector } from 'src/store/hooks';
import { ExecutionResponse } from 'src/store/slices/execution/executionState';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';

/** Payment lookup result: state values, or null when the ID wasn't found. */
type PaymentLookup = { status: string; state: string } | null;

/** Maps a beacon execution state to a Cloudscape StatusIndicator type. */
const stateToIndicatorType = (state: string | null): 'success' | 'error' | 'in-progress' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'in-progress';
};

const PAYMENTS_PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 payments' },
  { value: 25, label: '25 payments' },
  { value: 50, label: '50 payments' },
  { value: 100, label: '100 payments' },
];

const PAYMENTS_DEFAULT_PAGE_SIZE = 25;

interface FileDetailsPanelProps {
  execution: ExecutionResponse;
}

export const FileDetailsPanel = ({ execution }: FileDetailsPanelProps) => {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAYMENTS_DEFAULT_PAGE_SIZE);
  const [showCopyAllTip, setShowCopyAllTip] = useState(false);

  const allPaymentIds = execution.paymentIds ?? [];

  const filteredPaymentIds = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return allPaymentIds;
    return allPaymentIds.filter((id) => id.toLowerCase().includes(query));
  }, [allPaymentIds, filterText]);

  const pagesCount = Math.max(1, Math.ceil(filteredPaymentIds.length / pageSize));

  const pagedPaymentIds = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPaymentIds.slice(start, start + pageSize);
  }, [filteredPaymentIds, currentPage, pageSize]);

  // Payment status lookup — fetched lazily for the visible page only
  // (FetchPayments caps at 100 IDs; page size never exceeds 100).
  // Results are cached across pages; undefined = not yet fetched.
  const apiBase = useAppSelector((state) => state.env.apiBase);
  const [paymentLookups, setPaymentLookups] = useState<Record<string, PaymentLookup>>({});

  useEffect(() => {
    const missing = pagedPaymentIds.filter((id) => !(id in paymentLookups)).slice(0, 100);
    if (missing.length === 0) return;
    let cancelled = false;
    beaconApi
      .fetchPayments(missing, apiBase)
      .then((result) => {
        if (cancelled) return;
        setPaymentLookups((prev) => {
          const next = { ...prev };
          result.payments.forEach((p) => {
            next[p.id] = { status: p.beaconPaymentStatus, state: p.beaconPaymentState };
          });
          result.notFound.forEach((id) => { next[id] = null; });
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Mark as unknown so cells show '—' instead of retrying forever.
        setPaymentLookups((prev) => {
          const next = { ...prev };
          missing.forEach((id) => { if (!(id in next)) next[id] = null; });
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagedPaymentIds, apiBase]);

  const overviewItems = [
    { label: 'Execution ID', value: <CopyableText value={execution.id} /> },
    { label: 'Type', value: <Badge>{execution.executionType}</Badge> },
    { label: 'Status', value: execution.beaconExecutionStatus || '—' },
    {
      label: 'State',
      value: execution.beaconExecutionState ? (
        <StatusIndicator type={stateToIndicatorType(execution.beaconExecutionState)}>
          {execution.beaconExecutionState}
        </StatusIndicator>
      ) : ('—'),
    },
    { label: 'Data source', value: execution.dataSource || '—' },
    { label: 'Plugin', value: execution.pluginName || '—' },
    {
      label: 'File names',
      value: execution.executionTags?.length ? (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {execution.executionTags.map((tag) => (
            <Badge key={tag} color="blue">{tag}</Badge>
          ))}
          <CopyToClipboard
            variant="icon"
            textToCopy={execution.executionTags.join('\n')}
            copySuccessText="File names copied"
            copyErrorText="Failed to copy file names"
          />
        </SpaceBetween>
      ) : ('—'),
    },
    { label: 'First status at', value: formatIsoTimestamp(execution.firstStatusAt) },
    { label: 'Created', value: formatIsoTimestamp(execution.createdAt) },
    { label: 'Updated', value: formatIsoTimestamp(execution.updatedAt) },
    { label: 'Total latency', value: formatLatency(execution.totalLatencyMs) },
  ];

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Overview</Header>}>
        <KeyValuePairs columns={3} items={overviewItems} />
      </Container>

      {execution.errorInfo && (
        <Container header={<Header variant="h2">Error Info</Header>}>
          <PhaseDetailsRenderer rawJson={execution.errorInfo} />
        </Container>
      )}

      <StatusTimeline
        title="Execution Lifecycle"
        description="Status phases with per-step latencies. Click Details for phase specifics."
        timeline={execution.statusTimeline}
      />

      <Table
        variant="container"
        sortingDisabled
        items={pagedPaymentIds.map((id) => ({ id }))}
        trackBy="id"
        columnDefinitions={[
          {
            id: 'id',
            header: 'Payment ID',
            cell: (item: { id: string }) => (
              <Link href={`/payment?id=${encodeURIComponent(item.id)}`} external>
                {item.id}
              </Link>
            ),
          },
          {
            id: 'state',
            header: 'State',
            cell: (item: { id: string }) => {
              const lookup = paymentLookups[item.id];
              if (lookup === undefined) {
                return <StatusIndicator type="loading">Loading</StatusIndicator>;
              }
              if (lookup === null) return '—';
              return (
                <StatusIndicator type={stateToIndicatorType(lookup.state)}>
                  {lookup.state}
                </StatusIndicator>
              );
            },
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item: { id: string }) => paymentLookups[item.id]?.status ?? '—',
          },
        ]}
        header={
          <Header
            variant="h2"
            counter={`(${allPaymentIds.length})`}
            actions={
              allPaymentIds.length > 0 ? (
                <span
                  style={{ position: 'relative', display: 'inline-block' }}
                  onMouseEnter={() => setShowCopyAllTip(true)}
                  onMouseLeave={() => setShowCopyAllTip(false)}
                >
                  {showCopyAllTip && (
                    <span
                      role="tooltip"
                      style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 6px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#000716',
                        color: '#ffffff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        pointerEvents: 'none',
                      }}
                    >
                      Copy all payment IDs
                    </span>
                  )}
                  <CopyToClipboard
                    variant="button"
                    textToCopy={allPaymentIds.join('\n')}
                    copyButtonText="Copy all"
                    copySuccessText="All payment IDs copied"
                    copyErrorText="Failed to copy payment IDs"
                  />
                </span>
              ) : undefined
            }
          >
            Payments
          </Header>
        }
        filter={
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Find a payment ID"
            countText={
              filterText
                ? `${filteredPaymentIds.length} match${filteredPaymentIds.length === 1 ? '' : 'es'}`
                : ''
            }
            onChange={({ detail }) => {
              setFilterText(detail.filteringText);
              setCurrentPage(1);
            }}
          />
        }
        empty={
          <Box textAlign="center" color="text-body-secondary">
            {filterText ? 'No payment IDs match the filter.' : 'No payments for this execution.'}
          </Box>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={pagesCount}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{ pageSize }}
            pageSizePreference={{ title: 'Page size', options: PAYMENTS_PAGE_SIZE_OPTIONS }}
            onConfirm={({ detail }) => {
              if (detail.pageSize && detail.pageSize !== pageSize) {
                setPageSize(detail.pageSize);
                setCurrentPage(1);
              }
            }}
          />
        }
      />

    </SpaceBetween>
  );
};
